import { launchSurvey } from '../engine/survey-launcher.js';

export function checkShowTiming(settings, survey, storage, can = {}, surveyType = 'onsite') {
    // Fallback for legacy format
    let displayImmediately = settings.displayImmediately;
    let triggerDelay = settings.triggerDelay;
    let triggerScroll = settings.triggerScroll;
    let triggerExitIntent = settings.triggerExitIntent;
    let triggerCustomJavascript = settings.triggerCustomJavascript;

    if (displayImmediately === undefined && settings.displayTime !== undefined) {
        displayImmediately = settings.displayTime === 'immediately';
        triggerDelay = {
            enabled: settings.displayTime === 'afterSeconds',
            seconds: settings.secondsAfterLoad ?? 15
        };
        triggerScroll = {
            enabled: settings.displayTime === 'halfScroll',
            percentage: settings.scrollPercentage ?? 50
        };
        triggerExitIntent = {
            enabled: settings.displayTime === 'exitInternet'
        };
        triggerCustomJavascript = {
            enabled: false,
            script: ''
        };
    }

    // Default if nothing is set
    if (displayImmediately === undefined &&
        !triggerDelay?.enabled &&
        !triggerScroll?.enabled &&
        !triggerExitIntent?.enabled &&
        !triggerCustomJavascript?.enabled) {
        displayImmediately = true;
    }

    const requiredTriggers = new Set([
        triggerDelay?.enabled && 'delay',
        triggerScroll?.enabled && 'scroll',
        triggerExitIntent?.enabled && 'exitIntent',
        triggerCustomJavascript?.enabled && 'customJavascript'
    ].filter(Boolean));
    const satisfiedTriggers = new Set();
    let launched = false;
    const cleanupFns = [];

    function trigger() {
        if (launched) return;
        launched = true;
        // The server confirms display (and hands out the session) before render
        launchSurvey(survey, surveyType, storage, can);
        cleanupFns.forEach(fn => {
            try { fn(); } catch(e) {}
        });
    }

    // Every enabled trigger must be met before the survey can be shown.
    function satisfyTrigger(name) {
        satisfiedTriggers.add(name);
        if (satisfiedTriggers.size === requiredTriggers.size) {
            trigger();
        }
    }

    // 1. Immediate Trigger. This only launches on its own when no other
    // triggers are enabled; otherwise those triggers must also be satisfied.
    if (displayImmediately && requiredTriggers.size === 0) {
        trigger();
        return;
    }

    // 2. Delay Trigger
    if (triggerDelay?.enabled) {
        const seconds = parseFloat(triggerDelay.seconds) ?? 15;
        const timerId = setTimeout(() => {
            satisfyTrigger('delay');
        }, seconds * 1000);
        cleanupFns.push(() => clearTimeout(timerId));
    }

    // 3. Scroll Trigger
    if (triggerScroll?.enabled) {
        const scrollPercentage = triggerScroll.percentage ?? 50;
        function triggerSurveyIfNeeded() {
            let windowHeight = window.innerHeight;
            let documentHeight = document.documentElement.scrollHeight;
            const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
            const scrolledPercent = ((scrollPosition + windowHeight) / documentHeight) * 100;

            if (scrolledPercent >= scrollPercentage) {
                satisfyTrigger('scroll');
            }
        }
        window.addEventListener('scroll', triggerSurveyIfNeeded);
        cleanupFns.push(() => window.removeEventListener('scroll', triggerSurveyIfNeeded));
    }

    // 4. Exit Intent Trigger
    if (triggerExitIntent?.enabled) {
        if (window.innerWidth > 1023) {
            function handleMouseLeave(event) {
                if (event.relatedTarget === null) {
                    satisfyTrigger('exitIntent');
                }
            }
            document.addEventListener('mouseout', handleMouseLeave);
            cleanupFns.push(() => document.removeEventListener('mouseout', handleMouseLeave));
        } else {
            let lastScrollY = window.scrollY;

            function handleScroll() {
                let currentScrollY = window.scrollY;
                if (currentScrollY < lastScrollY) {
                    satisfyTrigger('exitIntent');
                }
                lastScrollY = currentScrollY;
            }

            function handlePopState() {
                satisfyTrigger('exitIntent');
            }

            window.addEventListener('popstate', handlePopState);
            window.addEventListener('scroll', handleScroll);
            cleanupFns.push(() => {
                window.removeEventListener('popstate', handlePopState);
                window.removeEventListener('scroll', handleScroll);
            });
        }
    }

    // 5. Custom JavaScript Trigger
    if (triggerCustomJavascript?.enabled && triggerCustomJavascript.script) {
        try {
            const userFn = new Function(triggerCustomJavascript.script);
            const result = userFn();
            if (result === true) {
                satisfyTrigger('customJavascript');
            } else {
                const intervalId = setInterval(() => {
                    try {
                        if (userFn() === true) {
                            clearInterval(intervalId);
                            satisfyTrigger('customJavascript');
                        }
                    } catch (e) {}
                }, 500);
                cleanupFns.push(() => clearInterval(intervalId));
            }
        } catch (e) {
            console.error("PollHero Custom JS Trigger Error:", e);
        }
    }
}
